/**
 * Viewport helpers.
 *
 * The design prototype was drawn at desktop width and every page style is an
 * inline object, which a media query cannot reach. Two tools cover that:
 *
 *   · `fluid()` builds a `clamp()` string, which *is* legal inside an inline
 *     style. Anything that only needs to get smaller — type, padding, gaps —
 *     uses it, so no breakpoint bookkeeping is involved.
 *   · `useIsMobile()` / `useIsTouch()` for the cases where the layout genuinely
 *     changes shape rather than scale: the nav collapsing into a drawer, the
 *     account rail turning into a scroller, the overlay scrollbar going away on
 *     touch devices.
 *
 * Structural collapses that a class can express still live in styles.css.
 */

import { useCallback, useEffect, useState } from 'react';

/** Matches the breakpoints in styles.css. Keep the two in step. */
export const BREAKPOINTS = { narrow: 600, mobile: 900 } as const;

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Below the point where the desktop nav and multi-column grids stop working. */
export const useIsMobile = (): boolean => useMediaQuery(`(max-width: ${BREAKPOINTS.mobile}px)`);

/** Phone-width: single column even where tablet keeps two. */
export const useIsNarrow = (): boolean => useMediaQuery(`(max-width: ${BREAKPOINTS.narrow}px)`);

/**
 * The rendered width of one element, kept current.
 *
 * For layouts that answer to their own room rather than to the window's. A
 * table beside the 264px sidebar and the same table with the sidebar collapsed
 * have very different room at the same viewport width — the sidebar goes away at
 * 900px, so a table is *more* cramped at 950 than at 850 — and a viewport query
 * cannot tell which it is. Zero until the first measurement.
 *
 * A callback ref rather than an object ref, so an element that mounts later (a
 * table that first rendered its empty state) is still observed.
 */
export function useWidth<T extends HTMLElement>(): [(el: T | null) => void, number] {
  const [el, setEl] = useState<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return;
    // Delivered after layout and before paint, so a column that has to go is
    // gone on the first frame rather than drawn and then removed.
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.round(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);

  return [useCallback((node: T | null) => setEl(node), []), width];
}

/**
 * Which columns a table has no room for, by index.
 *
 * A column with `hideBelow` goes when the table is narrower than that, so the
 * values that are nice to have give way before the name is squeezed to nothing,
 * and before the whole table has to stack into cards at phone width. An
 * unmeasured table (width 0) hides nothing, which is what every table did before
 * this existed.
 */
export function hiddenColumns(columns: readonly { hideBelow?: number }[], width: number): Set<number> {
  const hidden = new Set<number>();
  if (width <= 0) return hidden;
  columns.forEach((col, i) => {
    if (col.hideBelow !== undefined && width < col.hideBelow) hidden.add(i);
  });
  return hidden;
}

/** No hover and no precise pointer — i.e. do not draw hover-only affordances. */
export const useIsTouch = (): boolean => useMediaQuery('(hover: none), (pointer: coarse)');

/**
 * A `clamp()` that interpolates linearly between two viewport widths, so a
 * 58px hero reaches its 34px floor exactly at a 360px phone rather than at some
 * arbitrary point decided by a hand-picked `vw` figure.
 */
export function fluid(min: number, max: number, fromVw = 360, toVw = 1200): string {
  const slope = (max - min) / (toVw - fromVw);
  const intercept = min - slope * fromVw;
  const vw = (slope * 100).toFixed(3);
  return `clamp(${min}px, calc(${intercept.toFixed(2)}px + ${vw}vw), ${max}px)`;
}
